<template>
  <div class="kind-brands-page">
    <div class="page-header">
      <div class="back-button" v-if="showBackButton">
        <el-button @click="goBack" icon="el-icon-arrow-left" circle title="返回上一頁"></el-button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <el-row :gutter="20">
        <el-col v-for="n in 6" :key="n" :xs="12" :sm="8" :md="6" :lg="4">
          <el-skeleton style="width: 100%; margin-bottom: 20px;" animated>
            <template #template>
              <el-skeleton-item variant="rect" style="height: 100px;" />
              <div style="padding: 14px;">
                <el-skeleton-item variant="p" style="width: 70%; margin-bottom: 10px;" />
                <el-skeleton-item variant="text" style="width: 100%;" />
              </div>
            </template>
          </el-skeleton>
        </el-col>
      </el-row>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="error-alert"
    />

    <el-empty
      v-if="!loading && !error && brands.length === 0"
      :description="`暫無 '${kindNameDisplay}' 分類下的品牌信息`"
      class="empty-state"
    >
      <el-button type="primary" @click="goHome">返回上一頁</el-button>
    </el-empty>

    <div v-if="!loading && !error && brands.length > 0" class="brands-grid">
      <el-card
        v-for="brand in brands"
        :key="brand.BrandName || brand.BrandName"
        class="brand-card"
        shadow="hover"
        @click="navigateToBrandProducts(brand.BrandName)"
        role="link"
        tabindex="0"
        @keyup.enter="navigateToBrandProducts(brand.BrandName)"
      >
        <div class="brand-card-content">
          <h3 class="brand-name">{{ brand.BrandName }}</h3>
          <div class="arrow-indicator">
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex'; // 引入 useStore，如果你的 API 調用通過 Vuex action
import { ArrowRightBold } from '@element-plus/icons-vue';
import shopServer from '../../services/shopManages';

const route = useRoute();
const router = useRouter();
const store = useStore(); // 初始化 store 實例

const kindName = ref(route.params.kindName || ''); // 從路由參數獲取 kind 名稱
const brands = ref([]); // 存儲從 API 獲取的品牌列表
const loading = ref(true); // 加載狀態
const error = ref(null); // 錯誤信息

// 從英文 key 映射為前端顯示用的中文名稱
const kindNameMap = {
  sink_and_dishwasher: '水槽&洗碗槽',
  flower_stand_and_pot: '花架&花盆',
  bed_head_cushion: '床頭靠墊/床頭軟包',
  landscape_painting: '山水畫',
  tatami_sofa: '塌塌米豆帶沙發',
  bench: '長凳子/長條椅',
  steel_shelf: '角鋼架/貨架',
  cat_tree_and_house: '貓爬架/貓窩/貓屋',
  outdoor_sofa: '戶外沙發',
  mannequin: '人台/服裝模特',
  mosquito_net: '蚊帳',
  fitness_equipment: '健身器材',
  wardrobe_and_rack: '衣櫥/衣櫃/衣架',
  mirror_cabinet: '鏡櫃',
  sofa_and_chair: '沙發/電腦椅/長椅',
  automotive_parts: '汽車配件'
};

// 計算屬性，用於在頁面上顯示格式化後的 Kind 名稱（中文）
const kindNameDisplay = computed(() => {
  if (!kindName.value) return '未知分類';
  return kindNameMap[kindName.value] || kindName.value;
});

// 計算屬性，判斷是否顯示返回按鈕 (如果瀏覽歷史中有上一頁)
const showBackButton = computed(() => window.history.length > 1);

// 根據 Kind 名稱獲取品牌列表的異步函數
const fetchBrandsByKind = async (currentKindName) => {
  if (!currentKindName) {
    error.value = '未指定產品類型';
    loading.value = false;
    brands.value = [];
    return;
  }

  loading.value = true;
  error.value = null;
  brands.value = []; // 清空舊數據

  try {

    const response = await shopServer.GetGoodKindType(currentKindName);
    if (response) {
      brands.value = response; // 假設 response.data 是品牌數組 [{name: 'Brand A', id: 'brand-a'}, ...]
    } else {
      brands.value = [];
    }

    if (brands.value.length === 0 && currentKindName) {
       console.warn(`未找到類型 "${currentKindName}" 下的品牌數據 (模擬數據中沒有)。`)
    }
  } catch (err) {
    console.error(`獲取類型 "${currentKindName}" 的品牌列表失敗:`, err);
    error.value = err.response?.data?.message || err.message || `無法加載 ${kindNameDisplay.value} 分類的品牌列表。請檢查網絡連接或稍後再試。`;
    brands.value = []; // 出錯時確保品牌列表為空
  } finally {
    loading.value = false;
  }
};

// 導航到選定品牌的商品列表頁面
const navigateToBrandProducts = (brandName) => {
  router.push({
    path: '/shoes/search', // 假設你的商品列表頁路徑是 /shoes
    query: {
      kinds: kindName.value,    // 當前的 Kind 名稱
      brands: brandName,        // 被點擊的品牌名稱
      // page: 1                // 可以選擇默認跳轉到第一頁
    },
  });
};

// 返回上一頁
const goBack = () => {
  router.back();
};

// 返回首頁
const goHome = () => {
  router.back();
  // router.push('/');
};

// 組件掛載時，使用初始的 kindName 獲取數據
onMounted(() => {
  fetchBrandsByKind(kindName.value);
});

watch(() => route.params.kindName, (newKindName, oldKindName) => {
  if (newKindName && newKindName !== oldKindName) { // 確保 newKindName 有值且確實改變了
    kindName.value = newKindName; // 更新組件內部的 kindName ref
    fetchBrandsByKind(newKindName); // 使用新的 kindName 重新獲取品牌數據
  }
});

</script>

<style scoped>
.kind-brands-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 25px; /* 增加上下內邊距 */
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
  min-height: calc(100vh - 50px); /* 確保頁面至少填滿視窗高度 (減去可能的頁眉高度) */
}

.page-header {
  text-align: center;
  margin-bottom: 45px; /* 增加與內容的間距 */
  position: relative;
  padding-top: 20px; /* 給頭部一些呼吸空間 */
}

.page-header h1 {
  font-size: 2.6rem; /* 調整標題大小 */
  color: #1f2937; /* 更深的灰色，增加對比度 */
  font-weight: 700; /* 加粗 */
  margin: 0 0 12px 0;
  line-height: 1.3;
  letter-spacing: -0.5px; /* 微調字間距 */
}

.back-button {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  /* 使返回按鈕更易於點擊 */
  padding: 5px;
}

.back-button .el-button {
  border-width: 1.5px; /* 略微加粗邊框 */
  color: #4b5563; /* 按鈕顏色 */
}
.back-button .el-button:hover {
  border-color: #3b82f6; /* Element Plus 藍色主題色 */
  background-color: #eff6ff; /* 淡藍色背景 */
  color: #3b82f6;
}

.loading-container {
  padding: 40px 0;
}

.error-alert {
  margin: 30px auto; /* 居中 */
  max-width: 700px; /* 限制寬度 */
}

.empty-state {
  padding: 60px 0; /* 增加上下內邊距 */
  text-align: center;
}
.empty-state >>> .el-empty__description p { /* 提高選擇器優先級以覆蓋默認樣式 */
    font-size: 1.1rem;
    color: #4b5563;
}
.empty-state .el-button {
    margin-top: 25px;
    padding: 10px 20px; /* 增大按鈕 */
    font-size: 0.95rem;
}

.brands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); /* 增加卡片最小寬度 */
  gap: 30px; /* 增加卡片間距 */
}

.brand-card {
  border-radius: 12px; /* 更大的圓角 */
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  overflow: hidden;
  background-color: #ffffff;
  border: 1px solid #e5e7eb; /* 淡灰色邊框 */
  display: flex; /* 使內容垂直居中 */
  flex-direction: column; /* 內容垂直排列 */
  justify-content: center; /* 垂直居中 */
}

.brand-card:hover,
.brand-card:focus-within {
  transform: translateY(-10px) scale(1.03);
  box-shadow: 0 12px 30px -10px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.08); /* 更精緻的陰影 */
  border-color: transparent; /* 懸停時隱藏邊框，讓陰影更突出 */
}

.brand-card-content {
  padding: 25px 30px; /* 增加內邊距 */
  text-align: center;
  position: relative;
  flex-grow: 1; /* 允許內容區域填充卡片 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 90px;
}

.brand-name {
  font-size: 1.6rem; /* 增大品牌名 */
  color: #111827; /* 近乎黑色 */
  margin: 0;
  font-weight: 600; /* 加粗 */
  line-height: 1.3; /* 調整行高 */
  word-break: break-word;
  transition: color 0.2s ease; /* 品牌名顏色過渡 */
}
.brand-card:hover .brand-name {
    color: #2563eb; /* Element Plus 藍色主題色 */
}

.arrow-indicator {
  position: absolute;
  right: 20px; /* 調整位置 */
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.4rem; /* 增大箭頭 */
  color: #9ca3af; /* 中等灰色 */
  opacity: 0;
  transition: opacity 0.3s ease, right 0.3s ease, color 0.2s ease;
}

.brand-card:hover .arrow-indicator,
.brand-card:focus-within .arrow-indicator {
  opacity: 1;
  right: 25px;
  color: #2563eb; /* Element Plus 藍色主題色 */
}

/* 響應式調整 */
@media (max-width: 992px) { /* 調整斷點 */
    .brands-grid {
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 25px;
    }
}

@media (max-width: 768px) {
  .page-header h1 {
    font-size: 2.1rem;
  }
  .brands-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); /* 再次調整 */
    gap: 20px;
  }
  .brand-card-content {
    padding: 20px 25px;
    min-height: 80px;
  }
  .brand-name {
    font-size: 1.4rem;
  }
  .back-button {
    left: -10px; /* 微調返回按鈕位置 */
  }
}

@media (max-width: 576px) { /* 新增更小屏幕斷點 */
  .kind-brands-page {
    padding: 20px 15px;
  }
  .page-header h1 {
    font-size: 1.9rem;
  }
  .brands-grid {
    grid-template-columns: 1fr; /* 單列 */
    gap: 18px;
  }
  .brand-card-content {
    padding: 20px;
    flex-direction: row;
    justify-content: space-between;
  }
  .brand-name {
    font-size: 1.3rem;
    text-align: left;
  }
  .arrow-indicator {
    position: static;
    transform: none;
    opacity: 0.8;
    font-size: 1.3rem;
  }
   .back-button {
    position: static; /* 在最小屏幕上讓返回按鈕回到文檔流 */
    transform: none;
    text-align: left;
    margin-bottom: 20px;
    padding-left: 0; /* 清除之前的 padding */
  }
  .page-header {
    text-align: center; /* 確保標題居中 */
    padding-top: 0;
  }
}
</style>